import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  List,
  ListItem,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Description as DescriptionIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = 'https://agent.1lb6ey6g0jkt.iconaccounting.ie';

const TranscriptsPage = () => {
  const [transcripts, setTranscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTranscript, setSelectedTranscript] = useState(null);
  const [showTranscriptDialog, setShowTranscriptDialog] = useState(false);
  const [transcriptDetails, setTranscriptDetails] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTranscripts();
  }, []);

  const fetchTranscripts = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/api/support/transcripts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sorted = [...response.data].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      setTranscripts(sorted);
    } catch (error) {
      console.error('Failed to fetch transcripts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTranscript = async (transcript) => {
    setSelectedTranscript(transcript);
    setShowTranscriptDialog(true);
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/api/support/transcript/${transcript.session_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTranscriptDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch transcript details:', error);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Less than 1 minute';
    if (diffMins < 60) return `${diffMins} minutes`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hours`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} days`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'finished':
        return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
      case 'ended':
        return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
      default:
        return 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'finished':
        return 'Finished by Agent';
      case 'ended':
        return 'Ended by Customer';
      default:
        return status;
    }
  };

  return (
    <Box>
      <AppBar 
        position="fixed"
        sx={{
          background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
          boxShadow: '0 4px 20px rgba(0, 170, 229, 0.3)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Toolbar sx={{ minHeight: '70px' }}>
          <IconButton
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ 
              mr: 2,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 600,
              fontSize: '1.25rem'
            }}
          >
            Chat Transcripts ({transcripts.length})
          </Typography>
          <IconButton
            color="inherit"
            onClick={fetchTranscripts}
            disabled={loading}
            sx={{ 
              borderRadius: '50%',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : <DescriptionIcon />}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 8, p: 3 }}>
        <Grid container spacing={3}>
          {transcripts.map((transcript) => (
            <Grid item xs={12} md={6} lg={4} key={transcript.session_id}>
              <Card sx={{ 
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ChatIcon sx={{ 
                      color: '#667eea', 
                      mr: 1,
                      fontSize: 24
                    }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: '#2d3748'
                      }}
                    >
                      {transcript.user_name || 'Anonymous Customer'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={getStatusLabel(transcript.status)}
                      sx={{
                        background: getStatusColor(transcript.status),
                        color: 'white',
                        fontWeight: 500,
                        fontSize: '0.875rem'
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#64748b',
                        mb: 1
                      }}
                    >
                      <strong>Messages:</strong> {transcript.message_count}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#64748b',
                        mb: 1
                      }}
                    >
                      <strong>Started:</strong> {formatTime(transcript.created_at)}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#64748b',
                        mb: 1
                      }}
                    >
                      <strong>Ended:</strong> {formatTime(transcript.updated_at)}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#64748b'
                      }}
                    >
                      <strong>Duration:</strong> {getDuration(transcript.created_at, transcript.updated_at)}
                    </Typography>
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewTranscript(transcript)}
                                         sx={{
                       background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
                       borderRadius: '12px',
                       py: 1.5,
                       textTransform: 'none',
                       fontWeight: 600,
                       fontSize: '1rem',
                       boxShadow: '0 4px 12px rgba(0, 170, 229, 0.3)',
                       '&:hover': {
                         background: 'linear-gradient(135deg, #0099cc 0%, #0088b3 100%)',
                         transform: 'scale(1.02)',
                         boxShadow: '0 6px 16px rgba(0, 170, 229, 0.4)'
                       },
                       transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                     }}
                  >
                    View Transcript
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {transcripts.length === 0 && !loading && (
          <Box sx={{ 
            textAlign: 'center', 
            mt: 8,
            p: 4
          }}>
            <DescriptionIcon sx={{ 
              fontSize: 64, 
              color: '#cbd5e0',
              mb: 2
            }} />
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#64748b',
                fontWeight: 500,
                mb: 1
              }}
            >
              No Transcripts Available
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#94a3b8'
              }}
            >
              Completed chat sessions will appear here
            </Typography>
          </Box>
        )}
      </Container>

      {/* Transcript Details Dialog */}
      <Dialog
        open={showTranscriptDialog}
        onClose={() => setShowTranscriptDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle          sx={{ 
           background: 'linear-gradient(135deg, #00aae5 0%, #0099cc 100%)',
           color: 'white',
           borderRadius: '20px 20px 0 0',
           fontWeight: 600
         }}>
          Chat Transcript
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {transcriptDetails ? (
            <Box>
              <Box sx={{ mb: 3, p: 2, background: 'white', borderRadius: '12px' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {transcriptDetails.session.user_name || 'Anonymous Customer'}
                </Typography>
                    {/* Session ID hidden from agents */}
                <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                  <strong>Started:</strong> {formatTime(transcriptDetails.session.created_at)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  <strong>Ended:</strong> {formatTime(transcriptDetails.session.updated_at)}
                </Typography>
              </Box>

              <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Messages ({transcriptDetails.messages.length})
                </Typography>
                {transcriptDetails.messages.map((message, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: message.sender_type === 'support' ? 'flex-end' : 'flex-start',
                      mb: 2
                    }}
                  >
                    <Paper
                      sx={{
                        p: 2,
                        maxWidth: '80%',
                        background: message.sender_type === 'support' 
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        color: message.sender_type === 'support' ? 'white' : '#2d3748',
                        borderRadius: '18px',
                        boxShadow: message.sender_type === 'support'
                          ? '0 4px 12px rgba(102, 126, 234, 0.3)'
                          : '0 2px 8px rgba(0, 0, 0, 0.1)',
                        border: message.sender_type === 'support' 
                          ? 'none'
                          : '1px solid rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                        {message.message}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          opacity: message.sender_type === 'support' ? 0.8 : 0.6,
                          fontSize: '0.75rem',
                          display: 'block',
                          mt: 1
                        }}
                      >
                        {formatTime(message.timestamp)}
                      </Typography>
                    </Paper>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button 
            onClick={() => setShowTranscriptDialog(false)}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TranscriptsPage;
